import React, { memo, useEffect } from 'react'
import { Grid } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import { formatTicks } from 'src/util/string.helpers'
import Text from 'src/components/shared/text/Text'
import { QB_ACTION_SUBMIT } from '../../../constants/input'
import CustomButton from '../../shared/buttons/CustomButton'
import CustomForm from '../../shared/forms/customForm/CustomForm'
import Input from '../../shared/inputs/Input'
import { POINT_VALUE_MIN_LENGTH } from '../../../constants/validation'
import {
  checkValidations,
  positiveNumberRule,
  requiredRule,
  transWithArgs,
  validPointValueRule
} from '../../../util/validation.helpers'
import CustomSkeleton from '../../shared/CustomSkeleton/CustomSkeleton'
import { TOP_UP_MILES_DISPATCH } from 'src/store/reducers/exchange/topUpMiles'
import {
  changeTopUpMilesState,
  getMilesBalance,
  resetTopUpMilesState,
  submitTopUpMilesReq
} from '../../../store/actions/exchange'
import { containsNoValues } from '../../../util/object.helpers'
import DisplayLabel from '../../shared/DisplayLabel/DisplayLabel'

const TopUpMiles = ({ t }) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getMilesBalance())
    return () => {
      dispatch(resetTopUpMilesState())
    }
  }, [])

  const { isProcessing, loading, miles, milesValue, success, error } =
    useSelector((state) => state.exchangeReducer.topUpMilesProfile)

  const dispatchAction = (property, value) => {
    dispatch(changeTopUpMilesState(property, value))
  }

  const handleFormSubmit = () => {
    dispatchAction(TOP_UP_MILES_DISPATCH.ERROR, {})
    const errors = checkValidations({
      milesValue: {
        value: milesValue,
        rules: [requiredRule, positiveNumberRule]
      }
    })
    if (containsNoValues(errors)) {
      dispatch(submitTopUpMilesReq(milesValue))
    } else {
      dispatchAction(TOP_UP_MILES_DISPATCH.ERROR, errors)
    }
  }

  return (
    <Grid container spacing={3} mt={1}>
      <Grid item xs={12}>
        <Text
          label={t('exchange.your-miles-balance')}
          variant='h5'
          fontWeight={700}
        />
        <Text
          variant='h3'
          fontWeight={800}
          fontSize={28}
          label={
            loading ? (
              <CustomSkeleton sx={{ height: 52, width: 200 }} />
            ) : (
              `${formatTicks(miles)} ${t('exchange.miles')}`
            )
          }
        />
        <DisplayLabel
          label={t('exchange.top-up-miles-balance')}
          value={t('exchange.top-up-miles-balance-desc')}
          sx={{ mt: 1, fontSize: '1rem' }}
          valueProps={{ fontSize: '0.85rem' }}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomForm onFormSubmit={handleFormSubmit}>
          <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Grid
              item
              xs={12}
              md={9}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2
              }}
            >
              <Input
                id={TOP_UP_MILES_DISPATCH.MILES_VALUE}
                value={milesValue}
                validate={[validPointValueRule]}
                errorMessage={transWithArgs(
                  t,
                  error[TOP_UP_MILES_DISPATCH.MILES_VALUE]
                )}
                successMsg={transWithArgs(
                  t,
                  success[TOP_UP_MILES_DISPATCH.MILES_VALUE]
                )}
                onChange={(value) =>
                  dispatchAction(TOP_UP_MILES_DISPATCH.MILES_VALUE, value)
                }
                size='small'
                inputProps={{ maxLength: POINT_VALUE_MIN_LENGTH }}
              />
              <Text
                label={t('exchange.miles')}
                variant='h5'
                fontWeight={700}
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <CustomButton
                type={QB_ACTION_SUBMIT}
                label={t('exchange.top-up-miles')}
                fullWidth={false}
                disabled={isProcessing}
              />
            </Grid>
          </Grid>
        </CustomForm>
      </Grid>
    </Grid>
  )
}

export default memo(TopUpMiles)
